/* ─────────────────────────────────────────────────────────────
   SuspensionesModule.jsx — DMU 614
   Registro documental de días sin operación (clima/contingencia).
   ───────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import ModoDemoBanner from "../components/ModoDemoBanner";
import "../styles/supervisor.css";
import "../styles/modal-parque.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVO_META = {
  LLUVIA:       { label: "Lluvia",       icon: "fa-cloud-rain",        bg: "#DBEAFE", color: "#1E40AF" },
  NEVADA:       { label: "Nevada",       icon: "fa-snowflake",         bg: "#E0F2FE", color: "#075985" },
  CONTINGENCIA: { label: "Contingencia", icon: "fa-triangle-exclamation", bg: "#FEE2E2", color: "#991B1B" },
  OTRO:         { label: "Otro",         icon: "fa-circle-info",       bg: "#F1F5F9", color: "#475569" },
};

const ZONAS = ["Z1","Z2","Z3","Z4","Z5","Z6","Z7","Z8","Z9","Z10","Z11","Z12","Z13","Z14"];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" }); }
  catch { return iso; }
};

export default function SuspensionesModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verDemo, setVerDemo] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrar, setRegistrar] = useState(false);

  const puede = ["supervisor_general_pipas","admin"].includes(user?.rol);
  const esAdmin = user?.rol === "admin";

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/suspensiones`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (puede) cargar(); /* eslint-disable-next-line */ }, []);

  const activasHoy = useMemo(() => {
    const hoy = new Date().toISOString().slice(0,10);
    return items.filter(s => s.fecha === hoy && !s.anulada);
  }, [items]);

  const anular = async (s) => {
    if (!window.confirm(`¿Anular suspensión ${s.folio} del ${s.fecha}?`)) return;
    try {
      await axios.put(`${API}/suspensiones/${s.id}/anular`);
      toast.success(`Suspensión ${s.folio} anulada`);
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="sup-shell" data-testid="page-suspensiones">
      <ModoDemoBanner verDemo={verDemo} setVerDemo={setVerDemo} />
      <header className="sup-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/hub")} className="iconbtn-light" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <div className="sup-title"><i className="fa-solid fa-cloud-bolt mr-2"/>Suspensión de operación</div>
            <div className="sup-sub">{user?.nombre} · <span className="text-sky-300">{user?.rol}</span></div>
          </div>
        </div>
        <button onClick={logout} className="iconbtn-light" data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      <main className="sup-main">
        {!puede ? (
          <div className="text-center py-12 text-slate-500" data-testid="no-permisos">
            <i className="fa-solid fa-lock text-3xl mb-3"/>
            <div>Solo supervisor general y admin pueden gestionar suspensiones.</div>
          </div>
        ) : (
          <>
            {activasHoy.length > 0 && (
              <div className="susp-banner-hoy" data-testid="banner-activas-hoy">
                <i className="fa-solid fa-circle-exclamation"/>
                <strong>Hay {activasHoy.length} suspensión{activasHoy.length === 1 ? "" : "es"} activa{activasHoy.length === 1 ? "" : "s"} hoy.</strong>
                <span className="text-sm">Los choferes afectados verán el aviso en su app.</span>
              </div>
            )}

            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold m-0">Historial</h2>
              <button className="btn-primary" onClick={() => setRegistrar(true)} data-testid="btn-nueva-suspension">
                <i className="fa-solid fa-plus mr-1"/>Registrar suspensión
              </button>
            </div>

            {loading && <div className="text-center py-6 text-sm text-slate-500" data-testid="loading">Cargando…</div>}
            {!loading && items.length === 0 && (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="vacio">
                Sin suspensiones registradas.
              </div>
            )}

            <div className="space-y-2" data-testid="tabla-suspensiones">
              {items.map(s => {
                const m = MOTIVO_META[s.motivo] || MOTIVO_META.OTRO;
                return (
                  <article key={s.id} className="bandeja-card" data-testid={`susp-${s.folio}`} data-anulada={s.anulada}>
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div>
                        <div className="font-mono text-xs text-slate-500">{s.folio}</div>
                        <div className="font-semibold">{fmt(s.fecha)}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Registrada por {s.registrado_por_nombre} · {new Date(s.created_at).toLocaleString("es-MX", {dateStyle:"short", timeStyle:"short"})}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="pill-tiny" style={{background: m.bg, color: m.color, fontWeight: 700}} data-testid={`susp-motivo-${s.folio}`}>
                          <i className={`fa-solid ${m.icon} mr-1`}/>{m.label}
                        </span>
                        <span className="pill-tiny" style={{background: s.alcance === "TODA" ? "#0F172A" : "#7C2D12", color: "white", fontWeight: 700}} data-testid={`susp-alcance-${s.folio}`}>
                          {s.alcance === "TODA" ? "TODA la operación" : `${s.zonas_afectadas.join(", ")}`}
                        </span>
                        {s.anulada && <span className="pill-tiny" style={{background:"#FEE2E2", color:"#7F1D1D", fontWeight:700}} data-testid={`susp-anulada-${s.folio}`}>ANULADA</span>}
                      </div>
                    </div>
                    {s.comentario && <p className="text-sm bg-slate-50 rounded p-2 mt-2 mb-0">{s.comentario}</p>}
                    {!s.anulada && esAdmin && (
                      <div className="mt-2">
                        <button className="btn-ghost-sm" onClick={() => anular(s)} data-testid={`btn-anular-${s.folio}`}>
                          <i className="fa-solid fa-ban mr-1"/>Anular
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {registrar && (
              <DialogoRegistrar onClose={() => setRegistrar(false)} onOk={() => { setRegistrar(false); cargar(); }} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function DialogoRegistrar({ onClose, onOk }) {
  const hoy = new Date().toISOString().slice(0,10);
  const [fecha, setFecha] = useState(hoy);
  const [motivo, setMotivo] = useState("LLUVIA");
  const [alcance, setAlcance] = useState("TODA");
  const [zonas, setZonas] = useState([]);
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleZona = (z) => setZonas(zs => zs.includes(z) ? zs.filter(x => x !== z) : [...zs, z]);

  const enviar = async () => {
    if (alcance === "ZONAS" && zonas.length === 0) {
      toast.error("Selecciona al menos una zona o cambia el alcance a TODA");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/suspensiones`, {
        fecha, motivo, alcance,
        zonas_afectadas: alcance === "ZONAS" ? zonas : [],
        comentario: comentario || null,
      });
      toast.success(`Suspensión ${r.data.suspension.folio} registrada`);
      onOk();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="dlg-nueva-susp">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{maxWidth: 520}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title"><i className="fa-solid fa-cloud-bolt mr-2"/>Registrar suspensión</h2>
            <div className="mp-id">Quedará protocolizada como evidencia oficial</div>
          </div>
          <button onClick={onClose} className="mp-x" data-testid="btn-cerrar-dlg"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          <div>
            <label className="mp-validar-lbl">Fecha *</label>
            <input type="date" className="mp-select" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="input-fecha-susp"/>
          </div>
          <div>
            <label className="mp-validar-lbl">Motivo *</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(MOTIVO_META).map(([k, m]) => (
                <button
                  key={k} type="button"
                  className="motivo-chip"
                  onClick={() => setMotivo(k)}
                  data-testid={`btn-motivo-${k}`}
                  data-active={motivo === k}
                  style={{
                    background: motivo === k ? m.color : m.bg,
                    color: motivo === k ? "white" : m.color,
                    borderColor: m.color,
                  }}
                >
                  <i className={`fa-solid ${m.icon} mr-1`}/>{m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mp-validar-lbl">Alcance *</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="alc" checked={alcance === "TODA"} onChange={() => setAlcance("TODA")} data-testid="rad-alcance-toda"/>
                <span>TODA la operación</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="alc" checked={alcance === "ZONAS"} onChange={() => setAlcance("ZONAS")} data-testid="rad-alcance-zonas"/>
                <span>Zonas específicas</span>
              </label>
            </div>
            {alcance === "ZONAS" && (
              <div className="grid grid-cols-7 gap-1.5 mt-2" data-testid="grid-zonas">
                {ZONAS.map(z => (
                  <button
                    key={z} type="button"
                    onClick={() => toggleZona(z)}
                    data-testid={`btn-zona-${z}`}
                    data-active={zonas.includes(z)}
                    style={{
                      padding: "0.4rem", borderRadius: "6px",
                      border: "1.5px solid #CBD5E1",
                      background: zonas.includes(z) ? "#0F172A" : "white",
                      color: zonas.includes(z) ? "white" : "#0F172A",
                      fontWeight: 600, fontSize: "0.8rem",
                    }}
                  >{z}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="mp-validar-lbl">Comentario (opcional)</label>
            <textarea rows={3} maxLength={400} value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Detalle de la suspensión…" data-testid="textarea-comentario"/>
          </div>
        </div>
        <footer className="mp-foot">
          <button className="btn-primary" disabled={busy} onClick={enviar} data-testid="btn-confirmar-susp">
            {busy ? "Registrando…" : "Registrar suspensión"}
          </button>
          <button className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}}>Cancelar</button>
        </footer>
      </div>
    </div>
  );
}
